import { type ReactNode, useRef, useState, useEffect } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { type FormData, EXPERIENCE_LEVELS, PERSONALITY_QUESTIONS } from "./types";
import "./StepReview.scss";

// ─── Review Section / Field ───────────────────────────────────────────────────

export function ReviewSection({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) {
  return (
    <div className="rah-review-section">
      <div className="rah-review-section__header">
        <span className="rah-review-section__title">{title}</span>
        <button type="button" className="rah-review-section__edit-btn" onClick={onEdit}>
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
          Edit
        </button>
      </div>
      <div className="rah-review-section__body">{children}</div>
    </div>
  );
}

export function ReviewField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rah-review-field">
      <span className="rah-review-field__label">{label}</span>
      <span className="rah-review-field__value">{value || "—"}</span>
    </div>
  );
}

// ─── Footer buttons (shared between natural footer and sticky bar) ────────────

function FooterButtons({
  onBack,
  onSubmit,
  isSubmitting,
}: {
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  return (
    <>
      <button type="button" className="rah-form-card__back-btn" onClick={onBack} disabled={isSubmitting}>
        <ChevronLeftIcon sx={{ fontSize: 22 }} />
        Back
      </button>
      <button
        type="button"
        className={`rah-form-card__submit-btn${isSubmitting ? " rah-form-card__submit-btn--loading" : ""}`}
        onClick={onSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? <span className="rah-submit-spinner" /> : <CheckIcon sx={{ fontSize: 16 }} />}
        {isSubmitting ? "Submitting…" : "Submit request"}
      </button>
    </>
  );
}

// ─── Step 5: Review ───────────────────────────────────────────────────────────

function StepReview({
  data,
  onBack,
  onEdit,
  onSubmit,
  isSubmitting = false,
  submitError = null,
}: {
  data: FormData;
  onBack: () => void;
  // eslint-disable-next-line no-unused-vars
  onEdit: (_step: number) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  submitError?: string | null;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [stickyStyle, setStickyStyle] = useState({ left: 0, width: 608 });

  // Mirror the card's exact horizontal position so the sticky bar aligns
  // perfectly regardless of scrollbar width or viewport size.
  useEffect(() => {
    const updatePosition = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setStickyStyle({ left: rect.left, width: rect.width });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  // Show sticky bar whenever the natural card footer is not visible in the
  // scroll container; hide it once the user scrolls down to the natural footer.
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return undefined;

    const scrollContainer = document.querySelector(".rah-content");

    const observer = new IntersectionObserver(([entry]) => setStickyVisible(!entry.isIntersecting), {
      root: scrollContainer,
      threshold: 0,
    });

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const experienceLabel =
    EXPERIENCE_LEVELS.find((e) => e.value === data.experienceLevel)?.label ?? data.experienceLevel;
  const startDateLabel = data.startDate
    ? data.startDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

  const hasPersonality = PERSONALITY_QUESTIONS.some((q) => !!(data[q.key] as string));
  const hasGoodToKnow = data.additionalNotes.trim() || data.idealCandidateDescription.trim();

  return (
    <>
      <div className="rah-form-card rah-form-card--review" ref={cardRef}>
        <div className="rah-form-card__header">
          <h2 className="rah-form-card__title">Here&apos;s what you&apos;re looking for</h2>
          <p className="rah-form-card__subtitle">
            Review your request before submitting. You can go back and edit anything.
          </p>
        </div>

        <div className="rah-form-card__fields" style={{ paddingBottom: 24 }}>
          {/* Basics */}
          <ReviewSection title="Basics" onEdit={() => onEdit(0)}>
            <div className="rah-review-grid">
              <ReviewField label="Role or job title" value={data.jobTitle} />
              {data.jobPostingUrl && (
                <ReviewField
                  label="Job posting link"
                  value={
                    <span className="rah-review-field__value--url" title={data.jobPostingUrl}>
                      {data.jobPostingUrl.length > 40 ? `${data.jobPostingUrl.slice(0, 40)}...` : data.jobPostingUrl}
                    </span>
                  }
                />
              )}
              <ReviewField label="Experience level" value={experienceLabel} />
              <ReviewField label="Ideal start date" value={startDateLabel} />
            </div>
          </ReviewSection>

          {/* Capabilities */}
          <ReviewSection title="Capabilities" onEdit={() => onEdit(1)}>
            <div className="rah-review-col">
              {data.tools.length > 0 && (
                <ReviewField
                  label="Tools"
                  value={
                    <div className="rah-review-chips">
                      {data.tools.map((t) => (
                        <span key={t} className="rah-review-chip">
                          {t}
                        </span>
                      ))}
                    </div>
                  }
                />
              )}
              {data.languages.length > 0 && (
                <ReviewField
                  label="Languages"
                  value={
                    <div className="rah-review-chips">
                      {data.languages.map((l) => (
                        <span key={l} className="rah-review-chip">
                          {l}
                        </span>
                      ))}
                    </div>
                  }
                />
              )}
            </div>
          </ReviewSection>

          {/* Work personality (optional) */}
          {hasPersonality && (
            <ReviewSection title="Work personality" onEdit={() => onEdit(2)}>
              <div className="rah-review-grid">
                {PERSONALITY_QUESTIONS.filter((q) => !!(data[q.key] as string)).map((q) => {
                  const label =
                    q.options.find((o) => o.value === (data[q.key] as string))?.label ?? (data[q.key] as string);
                  return <ReviewField key={q.key as string} label={q.question} value={label} />;
                })}
              </div>
            </ReviewSection>
          )}

          {/* Good to know (optional) */}
          {hasGoodToKnow && (
            <ReviewSection title="Good to know" onEdit={() => onEdit(3)}>
              <div className="rah-review-col">
                {data.additionalNotes.trim() && (
                  <ReviewField label="What should we avoid? (optional)" value={data.additionalNotes} />
                )}
                {data.idealCandidateDescription.trim() && (
                  <ReviewField
                    label="Anything else we should know? (optional)"
                    value={data.idealCandidateDescription}
                  />
                )}
              </div>
            </ReviewSection>
          )}
        </div>

        {submitError && <p className="rah-submit-error">{submitError}</p>}

        {/* Natural footer — observed to control sticky bar visibility */}
        <div className="rah-form-card__footer" ref={footerRef}>
          <FooterButtons onBack={onBack} onSubmit={onSubmit} isSubmitting={isSubmitting} />
        </div>
      </div>

      {/* Sticky footer — slides up when natural footer scrolls out of view */}
      <div
        className={`rah-review-sticky-footer${stickyVisible ? " rah-review-sticky-footer--visible" : ""}`}
        style={{ left: stickyStyle.left, width: stickyStyle.width }}
      >
        <FooterButtons onBack={onBack} onSubmit={onSubmit} isSubmitting={isSubmitting} />
      </div>
    </>
  );
}

export default StepReview;
